import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MultiLineChart from '../components/charts/MultiLineChart';

function DevTestPage() {
  const [range, setRange] = useState('24h');
  const [bagData, setBagData] = useState([]);
  const [flocData, setFlocData] = useState([]);
  const [witData, setWitData] = useState([]);
  const [ratioData, setRatioData] = useState([]);
  const [flocRatioData, setFlocRatioData] = useState([]);
  const [labels, setLabels] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`/api/test-ewon-data.php?time_range=${range}`)
      .then(res => res.json())
      .then(res => {
        const rows = res.data || [];
        const bagVals = rows.map(r => r.m_bag);
        const flocVals = rows.map(r => r.floc_cup);
        const witVals = rows.map(r => r['100-WIT-1001']);
        const ratioVals = rows.map(r => {
          const plantFeed = r['100-WIT-1001'];
          const magnetite = r.m_bag;
          return plantFeed > 30 ? magnetite / plantFeed : null;
        });
        const flocRatioVals = rows.map(r => {
          const plantFeed = r['100-WIT-1001'];
          const floc = r.floc_cup;
          return plantFeed > 30 ? floc / plantFeed : null;
        });
        setBagData(bagVals);
        setFlocData(flocVals);
        setWitData(witVals);
        setRatioData(ratioVals);
        setFlocRatioData(flocRatioVals);
        setLabels(rows.map(r => {
          const d = new Date(r.timestamp);
          return range === '24h'
            ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : d.toLocaleDateString('en-GB');
        }));
      })
      .catch(() => {
        setBagData([]);
        setFlocData([]);
        setWitData([]);
        setRatioData([]);
        setFlocRatioData([]);
        setLabels([]);
      });
  }, [range]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex justify-center space-x-2 flex-1">
          <button
            className={`px-3 py-1 rounded ${range === '24h' ? 'bg-blue-600' : 'bg-blue-800'}`}
            onClick={() => setRange('24h')}
          >
            1 Day
          </button>
          <button
            className={`px-3 py-1 rounded ${range === '7d' ? 'bg-blue-600' : 'bg-blue-800'}`}
            onClick={() => setRange('7d')}
          >
            1 Week
          </button>
          <button
            className={`px-3 py-1 rounded ${range === '30d' ? 'bg-blue-600' : 'bg-blue-800'}`}
            onClick={() => setRange('30d')}
          >
            1 Month
          </button>
        </div>
        <button
          className="ml-4 px-3 py-1 bg-blue-600 rounded"
          onClick={() => navigate('/live')}
        >
          Back
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4">
            <MultiLineChart
              height="40vh"
              datasets={[
                { label: 'Plant Feed', data: witData, color: 'rgba(239,68,68,1)', borderWidth: 1 },
                {
                  label: 'Magnetite Feed Rate',
                  data: bagData,
                  color: 'rgba(59,130,246,1)',
                  borderWidth: 1,
                  yAxisID: 'y2',
                },
              ]}
              labels={labels}
              yLabel="t/hr"
              yLabel2="kg/hr"
              yColor="rgba(239,68,68,1)"
              y2Color="rgba(59,130,246,1)"
              xLabel="Time"
              chartTitle="Dev Test Data"
              showLegend={true}
              maxXTicks={10}
              pointRadius={0}
            />
          </div>
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4">
            <MultiLineChart
              height="40vh"
              datasets={[
                {
                  label: 'Magnetite Consumption',
                  data: ratioData,
                  color: 'rgba(34,197,94,1)',
                  borderWidth: 1,
                },
              ]}
              labels={labels}
              yLabel="kg_m/t_F"
              xLabel="Time"
              chartTitle="Magnetite Consumption Chart"
              showLegend={true}
              maxXTicks={10}
              pointRadius={0}
              tickStep={0.1}
              beginAtZero={true}
              tooltipLabelCallback={({ dataIndex }) => {
                const magnetite = bagData[dataIndex];
                const plantFeed = witData[dataIndex];
                if (magnetite == null || plantFeed == null) return '';
                return ['m_bag / 100-WIT-1001', `${magnetite} / ${plantFeed}`];
              }}
            />
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4">
            <MultiLineChart
              height="40vh"
              datasets={[
                { label: 'Plant Feed', data: witData, color: 'rgba(239,68,68,1)', borderWidth: 1 },
                {
                  label: 'Flocculant Feed Rate',
                  data: flocData,
                  color: 'rgba(168,85,247,1)',
                  borderWidth: 1,
                  yAxisID: 'y2',
                },
              ]}
              labels={labels}
              yLabel="t/hr"
              yLabel2="L/hr"
              yColor="rgba(239,68,68,1)"
              y2Color="rgba(168,85,247,1)"
              xLabel="Time"
              chartTitle="Flocculant Feed Data"
              showLegend={true}
              maxXTicks={10}
              pointRadius={0}
            />
          </div>
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4">
            <MultiLineChart
              height="40vh"
              datasets={[
                {
                  label: 'Flocculant Consumption',
                  data: flocRatioData,
                  color: 'rgba(34,197,94,1)',
                  borderWidth: 1,
                },
              ]}
              labels={labels}
              yLabel="L/t_F"
              xLabel="Time"
              chartTitle="Flocculant Consumption Chart"
              showLegend={true}
              maxXTicks={10}
              pointRadius={0}
              tickStep={0.1}
              beginAtZero={true}
              tooltipLabelCallback={({ dataIndex }) => {
                const floc = flocData[dataIndex];
                const plantFeed = witData[dataIndex];
                if (floc == null || plantFeed == null) return '';
                return ['floc_cup / 100-WIT-1001', `${floc} / ${plantFeed}`];
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DevTestPage;

